import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, Circle, X } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useOnboardingProgress, useDismissOnboarding } from '@/hooks/useDashboard';

interface OnboardingChecklistProps {
  enabled?: boolean;
}

export function OnboardingChecklist({ enabled = true }: OnboardingChecklistProps) {
  const { data, isLoading } = useOnboardingProgress({ enabled });
  const dismissMutation = useDismissOnboarding();

  if (!enabled || isLoading || !data) return null;
  if (data.dismissed || data.completedCount >= data.totalCount) return null;

  const progressPercent = Math.round((data.completedCount / data.totalCount) * 100);

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Get Started</h3>
          <p className="text-sm text-muted-foreground">
            Complete these steps to set up your organization
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground"
          onClick={() => dismissMutation.mutate()}
          disabled={dismissMutation.isPending}
          title="Dismiss checklist"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-muted-foreground">
            {data.completedCount} of {data.totalCount} steps complete
          </span>
          <span className="font-medium">{progressPercent}%</span>
        </div>
        <Progress value={progressPercent} className="h-2" />
      </div>

      <div className="space-y-2">
        {data.steps.map((step) => (
          <Link
            key={step.key}
            to={step.href}
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-accent group"
          >
            {step.completed ? (
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
            ) : (
              <Circle className="h-5 w-5 text-muted-foreground/50 flex-shrink-0 group-hover:text-primary" />
            )}
            <span className={step.completed ? 'text-muted-foreground line-through' : 'font-medium'}>
              {step.label}
            </span>
          </Link>
        ))}
      </div>
    </Card>
  );
}
